import React, { useEffect, useState } from 'react';
import ConfirmDialog from '../ConfirmDialog';
import { FIELD_LABEL_CLASS, FORM_INPUT_FIELD } from '../../constants/inputs';
import { AWAY_REASON_OPTIONS } from '../../utils/timesheetDisplay';

/**
 * "Away" (spec §8): from–to, or from + "until further notice", and a reason
 * (Holiday / Sick / Off contract / Other). Days in the range count as complete
 * and are never paid; "until further notice" takes the person off the lists
 * until the period is ended. Built on `ConfirmDialog`'s body slot — the house
 * modal, not a new overlay. `onConfirm({ fromDate, toDate, reason, note })`;
 * the caller posts it and shows the server's refusal (an overlap, inverted
 * dates) in `error`.
 */
const INPUT = FORM_INPUT_FIELD;

export default function AwayDialog({ open, personName, defaultFrom, busy, error, onConfirm, onCancel }) {
  const [fromDate, setFromDate] = useState(defaultFrom || '');
  const [toDate, setToDate] = useState('');
  const [untilFurtherNotice, setUntilFurtherNotice] = useState(false);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open) return;
    setFromDate(defaultFrom || '');
    setToDate('');
    setUntilFurtherNotice(false);
    setReason('');
    setNote('');
  }, [open, defaultFrom]);

  const datesOk = fromDate !== '' && (untilFurtherNotice || (toDate !== '' && toDate >= fromDate));
  const ready = datesOk && reason !== '';

  return (
    <ConfirmDialog
      open={open}
      title={`Set ${personName || 'this person'} as away`}
      confirmLabel="Set away"
      confirmVariant="primary"
      busy={busy}
      confirmDisabled={!ready}
      onConfirm={() => onConfirm({
        fromDate, toDate: untilFurtherNotice ? null : toDate, reason, note: note.trim() || null,
      })}
      onCancel={onCancel}
    >
      <div className="space-y-3 text-left" data-testid="away-dialog-body">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={FIELD_LABEL_CLASS} htmlFor="away-from">From</label>
            <input id="away-from" data-testid="away-from" type="date" className={INPUT}
              value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div>
            <label className={FIELD_LABEL_CLASS} htmlFor="away-to">To</label>
            <input id="away-to" data-testid="away-to" type="date" className={INPUT}
              value={untilFurtherNotice ? '' : toDate} disabled={untilFurtherNotice}
              min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)} />
          </div>
        </div>
        <label className="flex items-center gap-2 min-h-[44px] md:min-h-0 text-sm text-gray-800 dark:text-gray-200">
          <input type="checkbox" data-testid="away-until-further-notice" className="h-5 w-5"
            checked={untilFurtherNotice} onChange={(e) => setUntilFurtherNotice(e.target.checked)} />
          Until further notice
        </label>
        {untilFurtherNotice && (
          <p className="text-xs text-gray-600 dark:text-gray-400">
            They leave the lists from this date until you end the period.
          </p>
        )}
        <div>
          <label className={FIELD_LABEL_CLASS} htmlFor="away-reason">Reason</label>
          <select id="away-reason" data-testid="away-reason" className={INPUT}
            value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="">Choose a reason</option>
            {AWAY_REASON_OPTIONS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>
        <div>
          <label className={FIELD_LABEL_CLASS} htmlFor="away-note">Note (optional)</label>
          <input id="away-note" data-testid="away-note" type="text" className={INPUT} maxLength={2000}
            value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        {error && <p role="alert" className="text-sm text-red-700 dark:text-red-300">{error}</p>}
      </div>
    </ConfirmDialog>
  );
}
